import Vue from 'vue';
import VueRouter from 'vue-router';

Vue.use(VueRouter);

import routes from './routes';

const router = new VueRouter({
    mode: 'history',
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition)
            return savedPosition;
        return { x: 0, y: 0 };
    },
});

/*
 * vue-router rejects the promise returned by push() and replace() whenever a
 * navigation does not run to completion, and no caller here ever attached a
 * catch, so each one surfaced as an unhandled rejection with a full stack
 * trace. Two of them are ordinary events rather than faults:
 *
 *   duplicated - the link for the page already open was clicked, which the
 *                sidebar does on every navigation.
 *   cancelled  - a newer navigation replaced this one before it settled, which
 *                is what typing into a search box does on every keystroke.
 *
 * Both were filling storage/logs/browser.log with traces for behaviour that is
 * working as intended, which buries the entries worth reading.
 *
 * Only those two are swallowed, and only when the caller did not pass its own
 * callbacks. A guard that throws, a route component that fails to load, and a
 * redirected or aborted navigation all still reject and still reach the
 * console.
 */
// The ESM build vue-loader resolves exposes these as statics rather than as
// named exports, so they are read off the default export.
const { isNavigationFailure, NavigationFailureType } = VueRouter;

const IGNORED_NAVIGATION_FAILURES = [
    NavigationFailureType.duplicated,
    NavigationFailureType.cancelled,
];

['push', 'replace'].forEach((method) => {
    const navigate = router[method].bind(router);

    router[method] = function (location, onResolve, onReject) {
        // Passing callbacks opts out of the promise API, so there is nothing to catch.
        if (onResolve || onReject) {
            return navigate(location, onResolve, onReject);
        }

        return navigate(location).catch((error) => {
            if (IGNORED_NAVIGATION_FAILURES.some((type) => isNavigationFailure(error, type))) {
                return error;
            }

            return Promise.reject(error);
        });
    };
});

export default router;
